import React from 'react';
import './CelebrationPackagePicker.css';

const formatEGP = (cents) =>
  `EGP ${((cents || 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

// Card band colour by position in the list (silver, pink, gold, black), repeating
const TIERS = ['silver', 'pink', 'gold', 'black'];

/** Package total for a selection (same maths as the API, which is what is charged) */
export const celebrationTotalCents = (pkg, addonQuantity) =>
  pkg ? pkg.priceCents + (pkg.addonName ? (addonQuantity || 0) * (pkg.addonPriceCents || 0) : 0) : 0;

/**
 * "Special Occasion" row, styled like the other form fields: ticking it shows the
 * occasions (e.g. Birthday). Used instead of the plain Special Occasion tick box on
 * restaurants that offer occasions.
 */
export const SpecialOccasionHeader = ({ id, checked, onChange }) => (
  <label className={`occasion-row ${checked ? 'on' : ''}`} htmlFor={id}>
    <input
      type="checkbox"
      className="occasion-row-input"
      id={id}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="occasion-row-check" aria-hidden="true">
      <i className="bi bi-check-lg"></i>
    </span>
    <span className="occasion-row-text">
      <span className="occasion-row-title">Special Occasion</span>
      <span className="occasion-row-sub">Celebrating with us? Choose your occasion</span>
    </span>
    <i className="bi bi-stars occasion-row-icon" aria-hidden="true"></i>
  </label>
);

/**
 * The occasions (e.g. Birthday) as cards. Once one is picked: an optional note
 * and, when the occasion has packages, its package cards (one is required).
 *
 * value: { occasionId, packageId, addonQuantity }
 * errors: { occasion, package } highlight what the guest still has to pick
 */
export const OccasionPicker = ({ occasions, value, onChange, note, onNoteChange, errors = {} }) => {
  const selected = occasions.find((o) => o.id === value.occasionId) || null;
  const choose = (occasionId) => {
    if (occasionId !== value.occasionId) onChange({ occasionId, packageId: null, addonQuantity: 0 });
  };

  return (
    <div className="occasion-picker">
      <label className={`form-label mb-2 ${errors.occasion ? 'text-danger fw-bold' : ''}`}>
        What are you celebrating? {errors.occasion && <span className="text-danger">(required)</span>}
      </label>
      <div className={`occasion-cards ${errors.occasion ? 'invalid' : ''}`} role="radiogroup">
        {occasions.map((occasion) => {
          const isSelected = occasion.id === value.occasionId;
          return (
            <button
              key={occasion.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`occasion-card ${isSelected ? 'selected' : ''}`}
              onClick={() => choose(occasion.id)}
            >
              <span className="occasion-card-icon">
                <i className={`bi bi-${occasion.icon || 'stars'}`}></i>
              </span>
              <span className="occasion-card-name">{occasion.name}</span>
              <i className="bi bi-check-circle-fill occasion-card-tick"></i>
            </button>
          );
        })}
      </div>

      {selected && (
        <>
          <div className="mt-3">
            <input
              type="text"
              className="form-control modern-input"
              id="specialOccasion"
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder={`e.g., Sara's 30th ${selected.name.toLowerCase()}`}
              maxLength={120}
            />
            <small className="form-text text-muted">Anything we should know? (optional)</small>
          </div>

          {selected.packages.length > 0 && (
            <CelebrationPackagePicker
              occasionName={selected.name}
              packages={selected.packages}
              value={value}
              onChange={(selection) => onChange({ ...selection, occasionId: selected.id })}
              invalid={!!errors.package}
            />
          )}
        </>
      )}
    </div>
  );
};

/**
 * An occasion's packages: the guest must pick one and, when the package has an
 * add-on, how many of it. The price is paid with the booking.
 *
 * value: { packageId: number | null, addonQuantity: number }
 * invalid: highlight the cards when the guest tried to continue without one
 */
const CelebrationPackagePicker = ({ occasionName, packages, value, onChange, invalid = false }) => {
  const selected = packages.find((p) => p.id === value.packageId) || null;
  const total = celebrationTotalCents(selected, value.addonQuantity);

  const choose = (packageId) => {
    if (packageId !== value.packageId) onChange({ ...value, packageId, addonQuantity: 0 });
  };
  const changeAddon = (delta) => {
    const max = selected?.maxAddonQuantity || 0;
    const next = Math.min(max, Math.max(0, (value.addonQuantity || 0) + delta));
    onChange({ ...value, addonQuantity: next });
  };

  return (
    <div className="celebration-picker">
      <label className={`form-label mb-2 ${invalid ? 'text-danger fw-bold' : ''}`}>
        Choose your {occasionName.toLowerCase()} package {invalid && <span className="text-danger">(required)</span>}
      </label>
      <div className={`celebration-cards ${invalid ? 'invalid' : ''}`} role="radiogroup">
        {packages.map((pkg, index) => {
          const isSelected = pkg.id === value.packageId;
          return (
            <button
              key={pkg.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`celebration-card tier-${TIERS[index % TIERS.length]} ${isSelected ? 'selected' : ''}`}
              onClick={() => choose(pkg.id)}
            >
              <span className="celebration-card-band">
                {pkg.name}
                <i className="bi bi-check-circle-fill celebration-card-tick"></i>
              </span>
              <span className="celebration-card-body">
                {pkg.items?.length > 0 && (
                  <ul className="celebration-card-items">
                    {pkg.items.map((item, i) => (
                      <li key={i}>
                        <i className="bi bi-check-lg"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </span>
              <span className="celebration-card-price">{formatEGP(pkg.priceCents)}</span>
            </button>
          );
        })}
      </div>

      {selected?.addonName && (
        <div className="celebration-addon-row">
          <div>
            <div className="celebration-addon-name">{selected.addonName}</div>
            <small className="text-muted">{formatEGP(selected.addonPriceCents)} each</small>
          </div>
          <div className="ticket-quantity-controls">
            <button
              type="button"
              className="qty-btn minus"
              onClick={() => changeAddon(-1)}
              disabled={!value.addonQuantity}
              aria-label={`Fewer ${selected.addonName}`}
            >
              <i className="bi bi-dash"></i>
            </button>
            <span className="qty-value">{value.addonQuantity || 0}</span>
            <button
              type="button"
              className="qty-btn plus"
              onClick={() => changeAddon(1)}
              disabled={(value.addonQuantity || 0) >= selected.maxAddonQuantity}
              aria-label={`More ${selected.addonName}`}
            >
              <i className="bi bi-plus"></i>
            </button>
          </div>
        </div>
      )}

      {selected && (
        <div className="celebration-total">
          <span>{selected.name} package total</span>
          <strong>{formatEGP(total)}</strong>
        </div>
      )}
      <small className="form-text text-muted">
        Prices include taxes and service. The package is paid together with your booking.
      </small>
    </div>
  );
};

export default CelebrationPackagePicker;
